import React, { useState } from "react";

import { useAnalytics } from "@accurx/analytics";
import { useAuth, useCurrentUser, useLoginMethod } from "@accurx/auth";
import { Ds } from "@accurx/design";
import { cleanUpSession } from "appLifecycle";
import { Redirect, useLocation } from "react-router-dom";

import { ROUTES, UNAUTHED_ROUTES } from "shared/Routes";

import { InnerStackPanel, OuterStackPanel } from "../LogIn/LoginStackPanel";
import { StyledFullWidthButton } from "../SharedStyledComponents.styles";

enum PAGE_STATES {
    INITIAL,
    CHOOSE_PREVIOUS_ACCOUNT,
}

export const AlreadyLoggedIn = (): JSX.Element => {
    const { logOut } = useAuth();
    const { user } = useCurrentUser();
    const [currentState, setCurrentState] = useState(PAGE_STATES.INITIAL);
    const location = useLocation();
    const loginMethod = useLoginMethod();
    const track = useAnalytics();
    const urlParams = new URLSearchParams(location.search);

    const handleUsePreviousAccount = () => {
        track("AccountUserExistingLoginRemain Button Click", {
            loginMethod,
        });

        setCurrentState(PAGE_STATES.CHOOSE_PREVIOUS_ACCOUNT);
    };

    const handleSwitchAccounts = () => {
        track("AccountUserExistingLoginSwitch Button Click", {
            loginMethod,
        });

        // Force product=1 in the event it's been altered
        urlParams.set("product", "1");

        // log out and return to /login?product=1 so they can log in with a new account
        logOut({
            cleanUpFn: cleanUpSession,
            redirectTo: `${UNAUTHED_ROUTES.login}?${urlParams}`,
        });
    };

    const getContentToRender = () => {
        switch (currentState) {
            default:
            case PAGE_STATES.INITIAL:
                track("AccountUserExistingLogin Page View", {
                    loginMethod,
                });

                return (
                    <>
                        <Ds.Text as="h1" size="xxlarge">
                            You're already logged in
                        </Ds.Text>
                        <InnerStackPanel horizontalContentAlignment="stretch">
                            <Ds.Text>
                                You're already logged in as {user.email}.
                            </Ds.Text>
                            <StyledFullWidthButton
                                type="button"
                                onClick={handleSwitchAccounts}
                                text={"Switch to a different account"}
                            />
                            <StyledFullWidthButton
                                type="button"
                                theme="secondary"
                                onClick={handleUsePreviousAccount}
                                text={`Keep using ${user.email}`}
                            />
                        </InnerStackPanel>
                    </>
                );
            case PAGE_STATES.CHOOSE_PREVIOUS_ACCOUNT:
                return (
                    <Redirect
                        to={{
                            ...location,
                            pathname: ROUTES.desktopLaunch,
                        }}
                    />
                );
        }
    };

    return <OuterStackPanel>{getContentToRender()}</OuterStackPanel>;
};
